import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';


const SecurityComplianceSection = () => {
  const certifications = [
  {
    icon: 'ShieldCheck',
    title: 'SOC 2 Type II',
    description: 'Annual third-party audits verify security controls',
    status: 'Certified',
    color: '#10B981'
  },
  {
    icon: 'Lock',
    title: 'HIPAA Compliant',
    description: 'Full healthcare data protection standards',
    status: 'Certified',
    color: '#1A73E8'
  },
  {
    icon: 'Shield',
    title: 'ISO 27001',
    description: 'International information security management certification',
    status: 'Certified',
    color: '#10B981'
  },
  {
    icon: 'FileCheck',
    title: 'GDPR Ready',
    description: 'European data protection regulation standards',
    status: 'Certified',
    color: '#1A73E8'
  }];


  const securityFeatures = [
  {
    icon: 'Database',
    title: 'Enterprise-Grade Encryption',
    description: 'AES-256 encryption at rest and TLS 1.3 in transit for all data'
  },
  {
    icon: 'Users',
    title: 'Role-Based Access Control',
    description: 'Granular permissions and multi-factor authentication for all users'
  },
  {
    icon: 'Activity',
    title: 'Comprehensive Audit Logs',
    description: 'Complete activity tracking with tamper-proof logging for security'
  },
  {
    icon: 'Cloud',
    title: 'Redundant Infrastructure',
    description: 'Multi-region deployment with 99.9% uptime SLA guarantee'
  },
  {
    icon: 'AlertTriangle',
    title: 'Threat Detection',
    description: '24/7 security monitoring with automated threat response'
  },
  {
    icon: 'FileText',
    title: 'Data Residency Options',
    description: 'Choose data storage locations to meet data protection requirements'
  }];


  return (
    <section className="bg-[#1B1B20] text-white py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/30 rounded-full px-4 py-2 mb-4">
            <Icon name="ShieldCheck" size={16} color="#10B981" />
            <span className="text-xs md:text-sm font-medium text-[#10B981]">Enterprise Security</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            Security & Data Protection You Can Trust
          </h2>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
            Enterprise-grade security infrastructure with comprehensive certifications protecting your critical policy intelligence
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {certifications?.map((cert, index) =>
          <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${cert?.color}20` }}>
                <Icon name={cert?.icon} size={28} color={cert?.color} />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2">{cert?.title}</h3>
              <p className="text-sm text-gray-400 mb-3">{cert?.description}</p>
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cert?.color }}></div>
                <span className="text-xs font-medium">{cert?.status}</span>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 md:mb-16">
          <div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-6">
              Built for Enterprise Security Requirements
            </h3>
            <p className="text-base md:text-lg text-gray-300 mb-6 md:mb-8">
              PolicyAlerts meets the stringent security standards required by Fortune 500 medical device manufacturers. Our infrastructure is designed from the ground up with security, data protection, and reliability as core principles.
            </p>
            
            <div className="space-y-4">
              {securityFeatures?.map((feature, index) =>
              <div key={index} className="flex items-start gap-4 bg-white/5 rounded-lg p-4">
                  <div className="w-10 h-10 bg-[#1A73E8]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={feature?.icon} size={20} color="#1A73E8" />
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg font-semibold mb-1">{feature?.title}</h4>
                    <p className="text-sm text-gray-400">{feature?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
              <div className="aspect-[4/3] bg-gradient-to-br from-[#1A73E8]/20 to-[#0066FF]/20 rounded-xl mb-6 overflow-hidden">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1361fcc51-1765260388490.png"
                  alt="Digital security concept showing encrypted data streams and security shields protecting enterprise network infrastructure with multiple layers of protection"
                  className="w-full h-full object-cover opacity-80" />

              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle2" size={20} color="#10B981" />
                    <span className="text-sm md:text-base">Penetration Testing</span>
                  </div>
                  <span className="text-xs text-gray-400">Quarterly</span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle2" size={20} color="#10B981" />
                    <span className="text-sm md:text-base">Security Audits</span>
                  </div>
                  <span className="text-xs text-gray-400">Annual</span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle2" size={20} color="#10B981" />
                    <span className="text-sm md:text-base">Vulnerability Scanning</span>
                  </div>
                  <span className="text-xs text-gray-400">Continuous</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#1A73E8]/20 to-[#0066FF]/20 border border-[#1A73E8]/30 rounded-2xl p-6 md:p-8 lg:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div>
              <h3 className="text-xl md:text-2xl font-bold mb-4">Request Security Documentation</h3>
              <p className="text-base text-gray-300 mb-6">
                Access our comprehensive security whitepaper, data protection certifications, and penetration test results for your procurement evaluation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="default"
                  iconName="Download"
                  iconPosition="left"
                  className="bg-white text-[#1B1B20] hover:bg-gray-100">

                  Download Security Pack
                </Button>
                <Button
                  variant="outline"
                  iconName="Mail"
                  iconPosition="left"
                  className="border-white/30 text-white hover:bg-white/10">

                  Contact Security Team
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#10B981] mb-1">99.9%</div>
                <div className="text-xs md:text-sm text-gray-300">Uptime SLA</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#10B981] mb-1">24/7</div>
                <div className="text-xs md:text-sm text-gray-300">Security Monitoring</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#10B981] mb-1">&lt;15min</div>
                <div className="text-xs md:text-sm text-gray-300">Incident Response</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#10B981] mb-1">Zero</div>
                <div className="text-xs md:text-sm text-gray-300">Breaches Ever</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default SecurityComplianceSection;